// Parish-driven theme tokens.
//
// Applies a parish's accent color to the document root by setting
// the runtime-tunable CSS custom properties (--color-accent and
// derivatives). All other tokens are static and live in
// styles/global.css.
//
// HSL is used for the lighten/darken math so the derived hover
// color tracks the parish's hue. Contrast foreground is decided
// from relative luminance per WCAG 2.x — accents with luminance
// above the conventional 0.5 threshold get dark text on top,
// everything else gets white.

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(s: string): boolean {
  return HEX_RE.test(s);
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSL {
  h: number; // 0..360
  s: number; // 0..1
  l: number; // 0..1
}

function parseHex(hex: string): RGB {
  const h = hex.replace(/^#/, "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: RGB): string {
  const c = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      case bn:
        h = (rn - gn) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }: HSL): RGB {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = h / 360;
  return {
    r: hueToRgb(p, q, hk + 1 / 3) * 255,
    g: hueToRgb(p, q, hk) * 255,
    b: hueToRgb(p, q, hk - 1 / 3) * 255,
  };
}

function hueToRgb(p: number, q: number, t: number): number {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

export function darken(hex: string, pct: number): string {
  if (!isHexColor(hex)) return hex;
  const hsl = rgbToHsl(parseHex(hex));
  hsl.l = clamp(hsl.l - pct / 100, 0, 1);
  return toHex(hslToRgb(hsl));
}

export function lighten(hex: string, pct: number): string {
  if (!isHexColor(hex)) return hex;
  const hsl = rgbToHsl(parseHex(hex));
  hsl.l = clamp(hsl.l + pct / 100, 0, 1);
  return toHex(hslToRgb(hsl));
}

export function withAlpha(hex: string, alpha: number): string {
  if (!isHexColor(hex)) return hex;
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
}

// WCAG 2.x relative luminance.
function relativeLuminance({ r, g, b }: RGB): number {
  const ch = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

// "Should text on top of this color be dark or light?" — used to
// pick --color-accent-fg. Threshold of 0.5 is a conventional
// midpoint; deeper accents get white text, paler ones get near-black.
export function accentContrastFg(hex: string): string {
  if (!isHexColor(hex)) return "#FFFFFF";
  return relativeLuminance(parseHex(hex)) > 0.5 ? "#18181B" : "#FFFFFF";
}

// CSS custom properties the parish theme overrides inline on <html>.
// applyParishTheme writes them; applySacristyTheme clears them so
// the Sacristy theme's stylesheet defaults take over again.
const PARISH_INLINE_VARS = [
  "--color-accent",
  "--color-accent-hover",
  "--color-accent-fg",
  "--color-focus-ring",
  "--btn-hover-bg",
] as const;

// Switch the document to the parish theme and apply the parish's
// accent + derivatives. Sets data-theme="parish" so the CSS
// override block in global.css takes effect, then writes the
// per-parish accent inline (these inline values win over the
// data-theme defaults). Idempotent.
export function applyParishTheme(accent: string): void {
  if (typeof document === "undefined") return;
  const value = isHexColor(accent) ? accent : "#3B6FB0";
  const root = document.documentElement;
  root.dataset.theme = "parish";
  const s = root.style;
  s.setProperty("--color-accent", value);
  s.setProperty("--color-accent-hover", darken(value, 8));
  s.setProperty("--color-accent-fg", accentContrastFg(value));
  s.setProperty("--color-focus-ring", withAlpha(value, 0.4));
  s.setProperty("--btn-hover-bg", withAlpha(value, 0.08));
}

// Switch the document to Sacristy AI's own theme (parchment + gold
// + Playfair). Clears any inline accent overrides left behind by
// applyParishTheme so the stylesheet defaults under :root apply.
// Called when navigating into an authenticated scheduler surface.
export function applySacristyTheme(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = "sacristy";
  for (const v of PARISH_INLINE_VARS) root.style.removeProperty(v);
}
